import { getReportFooterText } from '../reportFooter';
export interface AllIeItem {
  lin?: string | null;
  nsn?: string | null;
  nomenclature: string;
  recoverableFlag?: string | null;
  costPerItem?: number | null;
  totalCostPerQtyAuth?: number | null;
  qtyAuthorized?: number | null;
}

export interface AllIeHeaderInfo {
  ieFilter?: string | null;
}

export interface AllIeKitSection {
  kitName: string;
  totalIeCost?: number | null;
  /** Average unit price (UPR) across items with a known cost, from the same catalog unit_price source as Cost Per Item. */
  upr?: number | null;
  /** Sum of qtyAuthorized across all items in the kit. */
  totalItemQuantity?: number | null;
  /** Count of distinct LINs authorized in the kit. */
  totalLinQuantity?: number | null;
  items: AllIeItem[];
}

export interface AllIeTemplateOptions {
  generatedAt: Date;
  header: AllIeHeaderInfo;
  kits: AllIeKitSection[];
  ieKitLabel?: string;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatCurrency = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const buildKitTableRows = (items: AllIeItem[], ieKitLabel: string): string => {
  if (!items.length) {
    return `
      <tr>
        <td colspan="7" class="empty-row">
          No ${ieKitLabel} items defined for this kit.
        </td>
      </tr>
    `;
  }

  return items
    .map(
      (item) => `
        <tr>
          <td>${sanitizeText(item.lin)}</td>
          <td>${sanitizeText(item.nsn)}</td>
          <td>${sanitizeText(item.nomenclature)}</td>
          <td>${sanitizeText(item.recoverableFlag)}</td>
          <td class="numeric">${formatCurrency(item.costPerItem ?? null)}</td>
          <td class="numeric">${formatCurrency(item.totalCostPerQtyAuth ?? null)}</td>
          <td class="numeric">${sanitizeText(
            item.qtyAuthorized !== null && item.qtyAuthorized !== undefined
              ? String(item.qtyAuthorized)
              : '',
          )}</td>
        </tr>
      `,
    )
    .join('');
};

const buildKitSummaryRows = (kit: AllIeKitSection, ieKitLabel: string): string => {
  // Hidden on-screen/print (values are shown in .kit-header instead) but kept inside
  // the <table> so the Excel export (which only reads <table> content) captures them.
  return `
    <tr class="summary-row">
      <td>Total ${ieKitLabel} Cost</td>
      <td class="numeric">${formatCurrency(kit.totalIeCost ?? null)}</td>
      <td>UPR</td>
      <td class="numeric">${formatCurrency(kit.upr ?? null)}</td>
      <td colspan="3"></td>
    </tr>
    <tr class="summary-row">
      <td>Total Item Quantity</td>
      <td class="numeric">${sanitizeText(String(kit.totalItemQuantity ?? 0))}</td>
      <td>Total LIN Quantity</td>
      <td class="numeric">${sanitizeText(String(kit.totalLinQuantity ?? 0))}</td>
      <td colspan="3"></td>
    </tr>
  `;
};

const buildKitSections = (kits: AllIeKitSection[], ieKitLabel: string): string => {
  if (!kits.length) {
    return `
      <section class="kit-section">
        <div class="kit-header">
          <span class="kit-name">No ${ieKitLabel.toLowerCase()}s available.</span>
        </div>
      </section>
    `;
  }

  return kits
    .map((kit) => {
      const totalCostLabel = formatCurrency(kit.totalIeCost ?? null);
      const rows = buildKitTableRows(kit.items, ieKitLabel);
      const summaryRows = buildKitSummaryRows(kit, ieKitLabel);
      return `
        <section class="kit-section">
          <div class="kit-header">
            <div class="kit-header-row">
              <span class="kit-label">${ieKitLabel} Name:</span>
              <span class="kit-name">${sanitizeText(kit.kitName)}</span>
              <span class="kit-label kit-cost-label">Total ${ieKitLabel} Cost:</span>
              <span class="kit-cost">${totalCostLabel}</span>
              <span class="kit-unit-label">UPR: ${formatCurrency(kit.upr ?? null)}</span>
              <span class="kit-unit-label">Total Item Quantity: ${sanitizeText(String(kit.totalItemQuantity ?? 0))}</span>
              <span class="kit-unit-label">Total LIN Quantity: ${sanitizeText(String(kit.totalLinQuantity ?? 0))}</span>
            </div>
          </div>

          <table class="kit-table" data-sheet-name="${sanitizeText(kit.kitName)}">
            <thead>
              <tr>
                <th>LIN</th>
                <th>NSN</th>
                <th>Nomenclature</th>
                <th>Recoverable / NonRecoverable</th>
                <th class="numeric">Cost Per Item</th>
                <th class="numeric">Total Cost per Qty Auth</th>
                <th class="numeric">Qty Auth</th>
              </tr>
            </thead>
            <tbody>
              ${summaryRows}
              ${rows}
            </tbody>
          </table>
        </section>
      `;
    })
    .join('');
};

export const buildAllIeReportHtml = ({
  generatedAt,
  header,
  kits,
  ieKitLabel = 'IE Kit',
}: AllIeTemplateOptions): string => {
  const generatedLabel = formatDateTime(generatedAt);
  const sections = buildKitSections(kits, ieKitLabel);
  const ieFilter =
    typeof header.ieFilter === 'string' && header.ieFilter.trim()
      ? sanitizeText(header.ieFilter)
      : '—';

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>All ${ieKitLabel}</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 24px;
      }

      .title {
        font-size: 28px;
        font-weight: 700;
        margin: 0;
        text-align: center;
      }

      .filters-row {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin-top: 12px;
      }

      .filter-block {
        min-width: 240px;
      }

      .filter-label {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 2px;
      }

      .filter-value {
        font-size: 14px;
        color: #15263c;
      }

      .generated {
        margin-top: 4px;
        font-size: 14px;
        color: #4a5c72;
        text-align: right;
      }

      .kit-section {
        margin-bottom: 24px;
        page-break-inside: avoid;
      }

      .kit-header {
        padding: 8px 12px;
        background: #e9f1ff;
        border-radius: 4px 4px 0 0;
        border: 1px solid #d7e3f4;
        border-bottom: none;
      }

      .kit-header-row {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: baseline;
      }

      .kit-label {
        font-size: 13px;
        font-weight: 600;
        color: #15263c;
      }

      .kit-name {
        font-size: 14px;
        font-weight: 600;
        color: #15263c;
      }

      .kit-cost-label {
        margin-left: auto;
      }

      .kit-currency-symbol {
        font-size: 14px;
        font-weight: 600;
      }

      .kit-cost {
        font-size: 14px;
        font-weight: 600;
      }

      .kit-unit-label {
        font-size: 12px;
        font-weight: 600;
        margin-left: 12px;
      }

      .kit-table {
        width: 100%;
        table-layout: fixed;
        border-collapse: collapse;
        border: 1px solid #d7e3f4;
      }

      .kit-table thead th:nth-child(1) { width: 8%; }   /* LIN */
      .kit-table thead th:nth-child(2) { width: 13%; }  /* NSN */
      .kit-table thead th:nth-child(3) { width: 28%; }  /* Nomenclature */
      .kit-table thead th:nth-child(4) { width: 14%; }  /* Recoverable / NonRecoverable */
      .kit-table thead th:nth-child(5) { width: 12%; }  /* Cost Per Item */
      .kit-table thead th:nth-child(6) { width: 15%; }  /* Total Cost per Qty Auth */
      .kit-table thead th:nth-child(7) { width: 10%; }  /* Qty Auth */

      .kit-table thead th {
        background: #f1f5ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 10px 12px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      .kit-table tbody td {
        padding: 10px 12px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      .kit-table tbody tr:last-child td {
        border-bottom: none;
      }

      .kit-table tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .kit-table th.numeric,
      .kit-table td.numeric {
        text-align: center;
        font-variant-numeric: tabular-nums;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      .summary-row {
        display: none;
      }

      footer {
        margin-top: 32px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <h1 class="title">All ${ieKitLabel}</h1>

      <div class="filters-row">
        <div class="filter-block">
          <div class="filter-label">${ieKitLabel} name (comma-separated)</div>
          <div class="filter-value">${ieFilter}</div>
        </div>
      </div>
      <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>
    </header>

    ${sections}

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
